/**
 * PostHog Analytics Plugin
 *
 * Integrates PostHog analytics into the Vue.js application
 * with graceful degradation and privacy features.
 */

import posthog from "posthog-js";
import posthogConfig from "@/utils/posthog.config";

/**
 * Capture UTM parameters from URL
 * Returns object with UTM values or empty object if none present
 */
function captureUTMParameters() {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams = {};

  const utmKeys = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "ref",
    "gclid",
  ];

  utmKeys.forEach((key) => {
    const value = urlParams.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });

  // Also capture referrer
  if (document.referrer && !urlParams.get("utm_source")) {
    try {
      const referrerUrl = new URL(document.referrer);
      utmParams.referrer = referrerUrl.hostname;
      utmParams.referring_domain = referrerUrl.hostname;
    } catch (e) {
      // Invalid referrer URL
    }
  }

  return utmParams;
}

/**
 * Register UTM parameters as person properties (only once)
 * This preserves the original acquisition source
 */
function registerUTMParameters(ph) {
  const utmParams = captureUTMParameters();

  if (Object.keys(utmParams).length > 0) {
    // Use $set_once to only set these properties if they don't already exist
    // This preserves the original acquisition source
    ph.people.set_once({
      ...utmParams,
      $initial_utm_source: utmParams.utm_source || null,
      $initial_utm_medium: utmParams.utm_medium || null,
      $initial_utm_campaign: utmParams.utm_campaign || null,
      $initial_utm_term: utmParams.utm_term || null,
      $initial_utm_content: utmParams.utm_content || null,
      $initial_referrer: utmParams.referrer || null,
      $initial_referring_domain: utmParams.referring_domain || null,
    });

    if (posthogConfig.debug) {
      console.log("[PostHog] Captured UTM parameters:", utmParams);
    }
  }
}

const PostHogPlugin = {
  install(Vue) {
    // Skip if no API key configured
    if (!posthogConfig.apiKey) {
      if (posthogConfig.debug) {
        console.log(
          "[PostHog] No API key configured - skipping initialization"
        );
      }
      return;
    }

    try {
      // Initialize PostHog
      posthog.init(posthogConfig.apiKey, {
        api_host: posthogConfig.apiHost,
        autocapture: posthogConfig.autocapture,
        session_recording: posthogConfig.sessionRecording,
        persistence: posthogConfig.persistence,
        capture_pageview: posthogConfig.capture_pageview,
        capture_pageleave: posthogConfig.capture_pageleave,
        ip_anonymization: posthogConfig.ip_anonymization,
        disable: posthogConfig.disable,
        loaded: (ph) => {
          if (posthogConfig.debug) {
            ph.debug();
          }
          // Start session recording
          if (posthogConfig.sessionRecording?.enabled) {
            ph.startSessionRecording();
          }
          // Capture UTM parameters on initial load
          registerUTMParameters(ph);
        },
        advanced_disable_decide: posthogConfig.advanced_disable_decide,
        disable_session_recording: posthogConfig.disable_session_recording,
        disable_compression: posthogConfig.disable_compression,
        disable_persistence: posthogConfig.disable_persistence,
        request_batching: posthogConfig.request_batching,
        request_queue_flush_interval:
          posthogConfig.request_queue_flush_interval,
        capture_copied_text: posthogConfig.capture_copied_text,
      });

      // Register global event properties
      // These will be included with every event
      posthog.register({
        app: 'my-app',
        environment: 'makeping'+(process.env.NODE_ENV === "production" ? "" : "-dev"),
        app_version: '2.0.0',
      });

      // Make PostHog available globally
      Vue.prototype.$posthog = posthog;
      window.$posthog = posthog;

      // Add opt-out capability
      Vue.prototype.$posthogOptOut = () => {
        posthog.opt_out_capturing();
      };

      // Add opt-in capability
      Vue.prototype.$posthogOptIn = () => {
        posthog.opt_in_capturing();
      };

      // Check if user has opted out
      Vue.prototype.$posthogHasOptedOut = () => {
        return posthog.has_opted_out_capturing();
      };

      if (posthogConfig.debug) {
        console.log("[PostHog] Plugin initialized successfully");
      }
    } catch (error) {
      console.error("[PostHog] Failed to initialize:", error);
    }
  },
};

export default PostHogPlugin;
export { posthog };
